import { flatten, get, groupBy, map, minBy, sortBy, uniq, uniqBy, mapValues } from 'lodash-es';
import { formatStyleDetails } from 'site-modules/shared/utils/features-specs/utils';
import {
  COLOR_SUMMARY_DISPLAY_MAP,
  EXCLUDED_SUMMARY_CATEGORIES,
  STEP_NUMBER,
  LEAD_FORM_OPTIONS_COMMENTS_MAP,
  COLOR_CATEGORY_NAME,
  VEHICLE_SUMMARY_SECTION,
  VEHICLE_SUMMARY_ALLOWED_SECTIONS,
  CONDITION_TYPE,
} from 'site-modules/shared/constants/build-price';
import { objectToQueryString } from 'site-modules/shared/utils/string';
import { getPriceString } from 'site-modules/shared/utils/price-utils';

export function getStyleIdList(stylesFeatures) {
  return stylesFeatures.map(({ id }) => id).join(',');
}

export function getModelYearById(modelYears, modelyearId) {
  return modelYears.find(({ id }) => id === modelyearId);
}

export function getModelYearByYear(modelYears, currentYear) {
  return modelYears.find(({ year }) => year === currentYear);
}

/**
 * Temporary solution to handle /favicon.ico invalid requests
 * @see https://edmunds.atlassian.net/browse/CORE-6392
 */
export function handleFaviconQuery(query) {
  return mapValues(query, paramValue => paramValue?.replace('/favicon.ico', ''));
}

export function getStyleFilterFromQuery(queryProps) {
  const styleFilter = {};

  ['submodel', 'modelyearId'].forEach(filter => {
    if (queryProps[filter]) {
      styleFilter[filter] = queryProps[filter];
    }
  });

  return styleFilter;
}

export function getAllBuildOptions({ optionIds, optionsList, optionsConfig, includedOptions }) {
  if (!optionsConfig) {
    return [];
  }

  return uniq(
    optionIds
      .split(',')
      .filter(id => id)
      .concat(includedOptions)
  )
    .map(optionId => optionsList.find(({ id }) => id === optionId))
    .filter(value => value);
}

export function getOptionsSummaryList({ optionIds, optionsList }) {
  if (!optionsList) {
    return [];
  }

  return optionIds
    .split(',')
    .map(optionId => optionsList.find(({ id }) => id === optionId))
    .filter(option => option && !EXCLUDED_SUMMARY_CATEGORIES.includes(option.category))
    .map(({ colorType, oemName }) => (colorType ? `${COLOR_SUMMARY_DISPLAY_MAP[colorType]}: ${oemName}` : oemName));
}

export function getStyleSummaryList(styleDetails) {
  if (!styleDetails) {
    return [];
  }

  const {
    mpg: { city, hwy },
    totalSeating,
    transmission,
  } = formatStyleDetails(styleDetails);

  return [`${city} city / ${hwy} hwy MPG`, `Seats ${totalSeating}`, transmission];
}

export function getVehicleSummarySections({
  year,
  buildPriceFilter: {
    styleFilter: { modelyearId, submodel },
    optionIds,
    styleId,
    step,
  },
  styleName,
  optionsConfig,
  optionsList,
}) {
  const step1Link = {
    url: `?${objectToQueryString({ defaultValue: modelyearId })}`,
    trackingId: 'build_summary_change_type',
  };
  const step15Link = {
    url: `?${objectToQueryString({ modelyearId, submodel, allStyles: true })}`,
    trackingId: 'build_summary_change_type',
  };
  const step2Link = {
    url: `?${objectToQueryString({ modelyearId, submodel, styleId, optionIds })}`,
    trackingId: 'build_summary_change_options',
  };

  const allBuildOptions = getAllBuildOptions({
    optionsConfig,
    optionsList,
    optionIds,
  });

  const colorItems = allBuildOptions
    .filter(({ category }) => category === 'colors')
    .map(({ colorType, oemName }) => `${COLOR_CATEGORY_NAME[colorType]}: ${oemName}`);
  const optionItems = allBuildOptions.filter(({ category }) => category !== 'colors').map(({ oemName }) => oemName);

  return [
    { name: VEHICLE_SUMMARY_SECTION.MODEL_YEAR, changeLink: step1Link, value: year },
    { name: VEHICLE_SUMMARY_SECTION.BODY_TYPE, changeLink: step1Link, value: submodel },
    { name: VEHICLE_SUMMARY_SECTION.TRIM, changeLink: step15Link, value: styleName },
    { name: VEHICLE_SUMMARY_SECTION.COLORS, changeLink: step2Link, items: colorItems },
    { name: VEHICLE_SUMMARY_SECTION.OPTIONS, changeLink: step2Link, items: optionItems },
  ].filter(
    ({ name, value, items }) => VEHICLE_SUMMARY_ALLOWED_SECTIONS[step].includes(name) && (value || get(items, 'length'))
  );
}

export function buildBreadcrumbs({ makeName, modelName, makeSlug, modelSlug }) {
  return [
    { title: 'Home', href: '/' },
    { title: makeName, href: `/${makeSlug}/` },
    { title: `${makeName} ${modelName}`, href: `/${makeSlug}/${modelSlug}/` },
    { title: 'Customize & Price', href: `/${makeSlug}/${modelSlug}/build/` },
  ];
}

export function getBuildPriceOptionsQuery({ styleFilter, styleId, optionIds }) {
  return `?${map(styleFilter, (value, key) => `${key}=${value}`).join('&')}&styleId=${styleId}&optionIds=${optionIds}`;
}

export function getReplacedColorIds(additions, optionsList) {
  const colorOptions = optionsList.filter(({ category }) => category === 'colors');
  const replacedTypes = additions.map(additionId =>
    get(
      colorOptions.find(({ id }) => id === additionId),
      'colorType'
    )
  );

  return colorOptions
    .filter(({ colorType, id }) => replacedTypes.includes(colorType) && !additions.includes(id))
    .map(({ id }) => id);
}

export function buildNextLink(buildPriceFilter, currentFilter, pathname) {
  const { styleFilter, styleId, urlOptionIds, step } = buildPriceFilter;

  const queryObject = {
    ...styleFilter,
    styleId,
    ...currentFilter,
    optionIds: styleId ? urlOptionIds : null,
  };

  return `${pathname}?${objectToQueryString(queryObject)}${step === STEP_NUMBER.STEP_1 ? '&allStyles' : ''}${
    styleId ? '&priceBreakdown' : ''
  }`;
}

/**
 * Groups styles by trim name,
 * transform each trim to array's element { trimName, baseMSRP, trimList },
 * sorts result array and trimList by baseMSRP
 *
 * @param stylesList
 */
export function transformStylesListToTrimList(stylesList) {
  return sortBy(
    map(groupBy(stylesList, 'trimName'), (trimList, trimName) => ({
      trimName,
      baseMSRP: get(
        minBy(trimList, ({ price }) => get(price, 'baseMSRP', 0)),
        'price.baseMSRP'
      ),
      trimList: sortBy(trimList, ({ price }) => get(price, 'baseMSRP', 0)),
    })),
    'baseMSRP'
  );
}

export function buildOptionsString(optionsList, optionIds) {
  const groupedOptions = groupBy(
    optionIds.split(',').map(optionId => optionsList.find(({ id }) => id === optionId)),
    ({ category, colorType }) => colorType || category
  );

  return LEAD_FORM_OPTIONS_COMMENTS_MAP.map(({ category, title }) =>
    groupedOptions[category] ? `${title}: ${groupedOptions[category].map(({ oemName }) => oemName).join(', ')}` : null
  )
    .filter(value => value)
    .join('; ');
}

export function getAdsCustomTargeting(step) {
  let sect8;
  if ([STEP_NUMBER.STEP_1, STEP_NUMBER.STEP_2, STEP_NUMBER.STEP_2_5].includes(step)) {
    sect8 = 'step1';
  } else if (STEP_NUMBER.STEP_3 === step) {
    sect8 = 'step2';
  } else if (STEP_NUMBER.STEP_4 === step) {
    sect8 = 'step3';
  }
  return { sect8 };
}

export function getOptionPrice(option, adjustments) {
  if (!option) {
    return {};
  }

  const adjustmentMSRP =
    adjustments &&
    get(
      adjustments.find(({ id }) => id === option.id),
      'price.baseMSRP',
      0
    );

  return {
    baseMSRP: (adjustmentMSRP || get(option, 'price.baseMSRP', 0)) + get(option, 'price.oemDiscountMsrp', 0),
  };
}

function resolveOptionsRules({ conditionType, options, subConditions }) {
  let resolvedOptions;

  switch (conditionType) {
    case CONDITION_TYPE.AND:
      resolvedOptions = [...options, ...(subConditions || []).map(resolveOptionsRules)];
      break;
    case CONDITION_TYPE.OR:
    case CONDITION_TYPE.XOR: // assuming XOR has only one option/subCondition with defaultChoice: true
      resolvedOptions = [
        ...(options || []).filter(({ defaultChoice }) => defaultChoice),
        ...(subConditions || []).filter(({ defaultChoice }) => defaultChoice).map(resolveOptionsRules),
      ];
      break;
    default:
      resolvedOptions = [];
  }

  return flatten(resolvedOptions).filter(value => value);
}

export function handleConfigurationRules({ styleName, optionPriceAdjustments, currentOptions, addOption, additions }) {
  const required = [];
  const included = flatten(
    get(additions, 'includes', []).map(condition => resolveOptionsRules(condition).map(({ id }) => id))
  );

  return {
    styleName,
    optionPriceAdjustments,
    included,
    allOptions: uniq([...currentOptions, ...required, ...included, addOption])
      .filter(value => value)
      .sort((a, b) => a - b),
  };
}

export function getPriceSummary({ tmv, isDealerless }) {
  const esp = tmv.totalWithOptions.tmv - tmv.incentivesAndRebates;
  const discountValue = esp - tmv.totalWithOptions.baseMSRP + tmv.incentivesAndRebates;
  const hasDiscount = esp <= tmv.totalWithOptions.baseMSRP;

  return {
    esp,
    espLabel: hasDiscount ? 'Edmunds Suggested Price' : 'What others are paying',
    espBreakdownLabel: hasDiscount ? 'Edmunds suggests you pay' : 'What others are paying',
    hasDiscount,
    items: [
      { label: 'Total MSRP', value: getPriceString(tmv.totalWithOptions.baseMSRP) },
      {
        hide: isDealerless,
        label: discountValue > 0 ? 'Average dealer markup' : 'Average dealer discount',
        value: `${discountValue <= 0 ? '' : '+'}${getPriceString(discountValue, '$0')}`,
        tooltipText:
          discountValue > 0
            ? `High demand is causing the market average price paid to be above MSRP. Shoppers in your area are paying an average markup of ${getPriceString(
                discountValue
              )}.`
            : `Shoppers in your area are getting an average discount of ${getPriceString(
                discountValue * -1,
                '$0'
              )} after negotiation. Dealer discounts vary based on negotiation and are not guaranteed.`,
      },
      { label: 'Incentives & rebates', value: `-${getPriceString(tmv.incentivesAndRebates, '$0')}` },
    ].filter(({ hide }) => !hide),
  };
}

export function getCurrentExteriorColor({ optionsList, optionIds }) {
  return get(
    optionsList.find(
      ({ id, category, colorType }) => category === 'colors' && colorType === 'EXTERIOR' && optionIds.includes(id)
    ),
    'oemName'
  );
}

export function getColorPhoto({ colorPhotos, optionsList, optionIds }) {
  if (!colorPhotos) {
    return null;
  }

  const currentColor = getCurrentExteriorColor({ optionsList, optionIds });
  return colorPhotos.find(({ color }) => color === currentColor);
}

export function getStepCriticalCss({ step, stylesList, optionsConfig, tmv }) {
  let stepSpecific;
  switch (step) {
    case STEP_NUMBER.STEP_2_5: {
      stepSpecific = {
        hasCollapse: !!stylesList && stylesList.length !== uniqBy(stylesList, 'trimName').length,
      };
      break;
    }
    case STEP_NUMBER.STEP_4: {
      stepSpecific = {
        hasData: !!(optionsConfig && tmv),
      };
      break;
    }
    default: {
      break;
    }
  }

  return { step, ...stepSpecific };
}
